/**
 * widgets/hooks.ts — data a dostupnost pro widgety.
 *
 *  - useNodeModules(node): jaké moduly cílový nod má (pro zašednutí = inactive)
 *  - useWidgetData(tool,args,scope): node-aware volání toolu se standardními stavy
 *
 * Widget tyto hooky sám nevolá — používá je WidgetShell, který z výsledků odvodí
 * jeden z pěti stavů. Widget dostane už jen hotová data v těle.
 */
import { useQuery } from '@tanstack/react-query';
import { callWidgetTool } from './scope';
import type { EffectiveScope } from './types';

/**
 * Množina jmen modulů nainstalovaných na cílovém nodu.
 * Lokál → acm-self.modules_list (MCP); remote → mesh peer call (callWidgetTool).
 * Chyba (nod nedostupný) → null = „nevím", shell pak nechá volání proběhnout
 * a případný fail se projeví jako error, ne inactive.
 */
export function useNodeModules(node: string | null) {
  const q = useQuery({
    queryKey: ['node-modules', node ?? 'local'],
    queryFn: async () => {
      const r = await callWidgetTool<{ ok?: boolean; modules: Array<{ name: string }> }>(
        'acm-self.modules_list',
        {},
        { node: node ?? null, identity: null },
      );
      // Absence znalosti ≠ absence modulu (Filip 2026-08-22). Když nod
      // neodpoví validním seznamem (ok:false, chybějící pole, starý kód bez
      // toolu), NESMÍ z toho být prázdný Set — ten by každý widget přeložil
      // na „Modul X na nodu není", i když modul běží. Throw → modules=null
      // = „nevím" a shell nechá volání proběhnout.
      if (!r || r.ok === false || !Array.isArray(r.modules)) {
        throw new Error('modules_list nevrátil validní seznam');
      }
      return new Set(r.modules.map((m) => m.name));
    },
    staleTime: 60_000,
    retry: 1,
  });
  return {
    modules: q.data ?? null, // null dokud nenačte / při chybě
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

/**
 * Node-aware volání toolu. Vrací surová data + stav načítání; interpretaci
 * (prázdno vs chyba) i mapování na WidgetState dělá WidgetShell.
 */
export function useWidgetData<T = unknown>(
  tool: string | undefined,
  args: Record<string, unknown>,
  scope: EffectiveScope,
  opts?: { enabled?: boolean; refetchInterval?: number },
) {
  return useQuery<T>({
    queryKey: ['widget-data', tool ?? '', scope.node ?? 'local', JSON.stringify(args)],
    queryFn: () => callWidgetTool<T>(tool as string, args, scope),
    enabled: (opts?.enabled ?? true) && !!tool,
    refetchInterval: opts?.refetchInterval,
    staleTime: 10_000,
    retry: 1,
  });
}
